import type { McpProbeStage, RemoteAgentTrustTier, RiskClass } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `admin.systems` — every word on the connected-systems surfaces.
 *
 * Extracted for the reason the other admin panels extract theirs — a sentence that decides
 * whether somebody understands what they are about to switch on does not belong buried in
 * JSX — and for one more that is specific to this plane.
 *
 * **The reader is not necessarily technical.** An administrator connecting a system may be
 * the person who owns the relationship with the vendor rather than the person who runs the
 * cluster. So the protocol word appears exactly once, on the line where it is a fact an
 * operator needs; everything else is written about a *system*, what it can *do*, and whose
 * account it does it with. A screen that says "MCP server", "streamable HTTP", "capability
 * kind" and "risk class" to that reader is a screen they hand back to somebody else.
 *
 * The failure copy follows one rule throughout: **name the stage, the cause, and whose fix
 * it is.** "Could not connect" is the sentence this whole plane exists to stop anybody
 * having to read.
 *
 * English is the source of truth AND the type (PRD-27 LOC-011): `Messages = typeof en`, so
 * the Portuguese sibling is checked against the shape of this file and a missing key is a
 * compile error rather than a runtime fallback. Anything that interpolates is a FUNCTION
 * returning a whole sentence, never a template assembled at the call site (LOC-013).
 *
 * The `: string` return annotations are not decoration. Without them a body that is a
 * ternary over string literals infers a literal UNION, and the Portuguese catalogue is then
 * typed into saying the English words.
 *
 * What is NOT here, by LOC-017/D-25: `NAME_MAX`, `DESCRIPTION_MAX` and `RISK_ORDER` live in
 * `components/marketplace/admin/system-limits.ts`, and the two guarded lookups that widen a
 * wire string onto a label map live in `system-labels.ts`. A validation limit that can drift
 * per locale is a bug, not a translation.
 */

/**
 * Just enough of the active locale for an entry that counts something.
 *
 * A parameter rather than an import, because a catalogue read at module scope freezes the
 * locale it was read in and then fails silently for the rest of the session (PRD-27 F-3).
 * The caller has `useI18n()`; it passes it in. Declared structurally rather than as a
 * `Pick<I18n, …>` so the messages tree does not have to name the type that contains it.
 */
export type Counting = { locale: Locale; formatCount: (value: number) => string }

export const systems = {
  title: 'Connected systems',
  lead: 'The outside systems this platform may reach, what each one can do, and who it does it as.',

  connect: 'Connect a system',
  empty: 'Nothing is connected yet',
  emptyBody:
    'Agents can search this organisation’s own knowledge without any of this. Connect a system when you want them to reach something else — a ticket tracker, a document store, a service desk.',

  loading: 'Loading connected systems…',
  unavailable: 'The list of connected systems could not be loaded',
  unavailableBody: 'This is the platform, not the systems. Nothing has been disconnected.',

  // --- list row -------------------------------------------------------------
  never: 'never checked',
  checked: (when: string): string => `checked ${when}`,
  /**
   * How many things a system said it can do.
   *
   * A catalogue entry owning both of its forms rather than an `n === 1` at the call site
   * (PRD-27 D-19): `Intl.PluralRules('pt-BR').select(0)` is `one`, so Portuguese takes the
   * singular at zero and the two-branch check this replaced is already wrong in one of the two
   * shipped languages. It was written twice — inline in `system-stages.ts` and again in the
   * connect flow's badge — which is the other half of why it lives here now.
   */
  toolCount: (fmt: Counting, count: number): string =>
    pluralCategory(fmt.locale, count) === 'one'
      ? `${fmt.formatCount(count)} thing it can do`
      : `${fmt.formatCount(count)} things it can do`,
  /**
   * `nothing discovered yet` is taken before the plural rules are consulted, because it is a
   * different statement from "0 available" rather than the zero form of it.
   */
  capabilityCount: (fmt: Counting, live: number, total: number): string =>
    total === 0
      ? 'nothing discovered yet'
      : live === 0
        ? `${fmt.formatCount(total)} available, none switched on`
        : `${fmt.formatCount(live)} of ${fmt.formatCount(total)} switched on`,
  disabled: 'Paused',
  disabledHint: 'No agent can reach this system. Its settings and decisions are kept.',
  open: 'Open',
  openLabel: (name: string): string => `Open ${name}`,
  /** The credential readout on a system's own page, when it was registered without one. */
  noCredential: 'no credential',

  // --- who can use it (PRD-26 D-25, GRP-060) ----------------------------------
  access: {
    title: 'Who can use it',
    // The rule is about the person in the conversation, not whose agent it is (D-25).
    lead: 'The people whose conversations may call this system, through any agent. A change applies from their next message.',
    remoteLead:
      'The people whose conversations may hand work to this remote agent, through any agent. A change applies from their next message.',
    label: 'Who can use it:',
    everyone: 'Everyone who signs in',
    onlyGroups: 'Only people in',
    more: (count: number): string => `+${count}`,
    moreLabel: (fmt: Counting, count: number): string =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `and ${fmt.formatCount(count)} more group`
        : `and ${fmt.formatCount(count)} more groups`,
    /** `groups` with none left: fail-closed, and said out loud rather than shown as an empty list. */
    nobody: 'Nobody can use it',
    nobodyHint:
      'The groups it was limited to no longer exist here, so no agent may use it. Choose who can use it again.',
    change: 'Change',
    changeLabel: (name: string): string => `Change who can use ${name}`,
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    saved: 'Saved. It applies from their next message.',
    stale:
      'Someone changed this while you were editing, so nothing was saved. The current setting is shown — choose again.',
    failed: 'That change was not saved. Try again.'
  },

  // --- remote agents (PRD-26 GRP-061) -----------------------------------------
  remote: {
    title: 'Remote agents',
    lead: 'Agents run by other organisations or systems, which this platform’s agents may hand work to.',
    empty: 'No remote agents are registered',
    emptyBody:
      'A remote agent is registered, reviewed and turned on through the administration API. Once one is, who can use it is chosen here.',
    loading: 'Loading remote agents…',
    unavailable: 'Remote agents could not be loaded',
    unavailableBody: 'This is the platform, not the remote agents. Nothing has been changed.',
    off: 'Off',
    offHint: 'No agent can hand work to it until an administrator reviews its card and turns it on.',
    trust: {
      untrusted: 'not reviewed',
      reviewed: 'reviewed',
      first_party: 'first party'
    } satisfies Record<RemoteAgentTrustTier, string>,
    /**
     * Nothing listed has its own sentence, taken before the plural rules are consulted — it is
     * a different statement from "0 skills", and in Portuguese zero is the SINGULAR category,
     * so a count-led phrasing would read "0 habilidade" there.
     */
    skills: (fmt: Counting, count: number): string =>
      count === 0
        ? 'no skills listed'
        : pluralCategory(fmt.locale, count) === 'one'
          ? `${fmt.formatCount(count)} skill`
          : `${fmt.formatCount(count)} skills`,
    unnamed: 'Card not read yet'
  },

  // --- the connect flow -----------------------------------------------------
  flow: {
    /** The server's own sentence for a taken name names the protocol; the product never does. */
    nameTaken: 'A connected system with that name already exists. Choose another name.',
    title: 'Connect a system',
    lead: 'Paste the system’s address. Everything else is worked out from what it answers.',
    addressLabel: 'Address',
    addressPlaceholder: 'https://example.com/mcp',
    addressHint:
      'The system’s own endpoint address, which its documentation usually calls a server or endpoint URL. Not its website.',
    check: 'Check it',
    checking: 'Asking the system…',
    recheck: 'Check again',
    cancel: 'Cancel',
    back: 'Back',

    stagesTitle: 'What the platform found',
    stagesLead: 'Each step, and what it means. A step is only attempted once the one above it passed.',

    /** The six stages, in the order they run. */
    stage: {
      address: 'Address',
      reach: 'Reachable',
      protocol: 'Speaks the protocol',
      identity: 'Says who it is',
      authorization: 'Credentials',
      offer: 'What it offers'
    },
    stagePending: 'not reached',
    stageChecking: 'checking',

    /**
     * One line of evidence under each step, which `system-stages.ts` used to hold as inline
     * literals. It is a pure module with no component body, so it takes this pack as a
     * parameter (LOC-012) rather than importing it — a module-scope read would freeze the
     * locale for the whole session with nothing failing anywhere.
     */
    evidence: {
      answered: (fmt: Counting, ms: number): string => `answered in ${fmt.formatCount(ms)} ms`,
      /** An earlier revision is NAMED as one, never reported as a problem: the platform speaks it. */
      protocolLegacy: (version: string): string => `${version} (an earlier revision, which this platform also speaks)`,
      callsItself: (name: string): string => `calls itself ${name}`,
      callsItselfVersioned: (name: string, version: string): string => `calls itself ${name} ${version}`,
      // "none needed" was asserted for every success, including the ones that succeeded
      // *because* a credential was sent — which reads to an administrator as "you can delete
      // that token reference". The probe now says which it was, rather than this guessing.
      credentialAccepted: 'the credential was accepted',
      credentialNone: 'none needed',
      /**
       * The authorization fork gets its own sentence rather than the wire message. What the
       * probe hands over is `MCP server "x" requires authorization (HTTP 401); its authority
       * is described at https://…` — four pieces of protocol vocabulary aimed at whoever
       * debugs the transport, shown to somebody who was told this screen would explain itself.
       */
      signsPeopleIn: 'This system signs people in. It issues its own credentials rather than accepting one you paste.'
    },

    claimTitle: 'What the system says about itself',
    claimHint: 'Its own words, not the platform’s. Nothing here has been verified.',
    noIdentity: 'This system did not say what it is called.',

    nameLabel: 'Short name',
    nameHint:
      'Used in audit records and in the address of this page. Lowercase letters, numbers and dashes. It cannot be changed later.',
    descriptionLabel: 'What it is, in your words',
    descriptionHint: 'Read by other administrators, not by any agent.',
    descriptionPlaceholder: 'The ticket tracker the service desk works in.',

    trustLabel: 'How far this system is trusted',
    trustHint:
      'This decides how much weight the platform gives what the system says about its own tools. It never decides what a tool may do — that stays with you.',
    trust: {
      untrusted: {
        label: 'Not reviewed',
        note: 'Nothing it says about its own tools counts. Every tool starts needing approval, and you classify each one by hand.'
      },
      reviewed: {
        label: 'Reviewed',
        note: 'Somebody has checked who runs this system. A tool it describes as read-only may be proposed as one, for you to accept.'
      },
      first_party: {
        label: 'Ours',
        note: 'This organisation runs this system. Its descriptions are treated as accurate, and you still confirm every one.'
      }
    },

    credentialTitle: 'This system wants a credential',
    credentialNone: 'This system did not ask for a credential.',
    credentialTokenLabel: 'Where the token is kept',
    credentialTokenPlaceholder: 'env:JIRA_TOKEN',
    credentialTokenHint:
      'The name of the place the token is stored, never the token. The platform reads it at the moment of a call and keeps no copy — so rotating the secret is enough, and nothing here has to change.',
    credentialOauthTitle: 'This system signs people in',
    credentialOauthBody:
      'It issues its own credentials rather than accepting one you paste. Connecting it opens its sign-in page in this window.',
    /**
     * The one genuinely consequential choice in this flow, so it is a choice and not a
     * default (PRD-23 D-6). One authority model per system: mixing them makes "whose
     * authority was this?" answerable only per call.
     */
    modeLabel: 'Whose account should it use?',
    modeHint:
      'This cannot be mixed. Every call to this system runs as one or the other, and the record of what happened says which.',
    mode: {
      delegated: {
        label: 'Each person’s own',
        note: 'Everyone signs in themselves the first time an agent needs it. The system’s own records show who really did what, and someone who leaves takes their access with them.'
      },
      service_account: {
        label: 'One account for everyone',
        note: 'You sign in once and every agent uses that account. Simpler, and the system on the other side cannot tell your colleagues apart — everything it logs will say it was you.'
      }
    },
    signIn: 'Connect and sign in',
    signingIn: 'Opening the sign-in page…',
    /** Shown when the browser comes back from the authority. */
    returnedOk: (name: string): string => `${name} is connected.`,
    returnedFailedTitle: 'That sign-in did not complete',
    scopeLabel: 'It will ask for',

    submit: 'Connect',
    submitting: 'Connecting…',
    connected: 'Connected',
    connectedBody: (fmt: Counting, count: number): string =>
      count === 0
        ? 'Nothing is switched on yet. Review what it offers to decide what agents may use.'
        : pluralCategory(fmt.locale, count) === 'one'
          ? `${fmt.formatCount(count)} thing to review. Nothing is switched on until you say so.`
          : `${fmt.formatCount(count)} things to review. Nothing is switched on until you say so.`,
    review: 'Review what it offers',

    /** Shown when the address is a command rather than a URL. */
    stdioTitle: 'That looks like a command, not an address',
    stdioBody:
      'Some systems are described as a command to run on your own machine. This platform will not run one: it would be running somebody else’s code beside the database and the model key. Ask the system’s owner for a hosted address.'
  },

  // --- the capability review ------------------------------------------------
  review: {
    title: 'What this system offers',
    lead: 'Nothing here can be used by an agent until you switch it on and say what it may do.',
    empty: 'Nothing discovered yet',
    emptyBody: 'Ask the system what it offers — the platform has not looked since it was connected.',
    discover: 'Ask again what it offers',
    discovering: 'Asking…',

    claimedBy: (system: string): string => `What ${system} says`,
    claimHint: 'The system’s own description. It has not been checked by anyone.',
    behaviourTitle: 'And what it says about how it behaves',
    behaviourNone: 'It says nothing about how this behaves.',

    hint: {
      readOnly: 'only reads, changes nothing',
      writes: 'changes something',
      destructive: 'destroys something',
      notDestructive: 'nothing it changes is destroyed',
      idempotent: 'running it twice is the same as running it once',
      openWorld: 'reaches outside the system',
      closedWorld: 'stays inside the system'
    },

    suggestionTitle: 'The platform suggests',
    suggestionAccept: 'Use this',
    classLabel: 'What it may do',

    enable: 'Switch on',
    disable: 'Switch off',
    enabled: 'On',
    notEnabled: 'Off',
    saving: 'Saving…',
    /** The checkbox on a row, which a screen reader reaches before the row's heading. */
    selectLabel: (name: string): string => `Select ${name}`,

    quarantinedTitle: 'Held back — this changed',
    reReviewTitle: 'Held back — never reviewed with this in view',
    clearQuarantine: 'I have read the change',
    refusedTitle: 'The platform will not carry this one',

    selected: (fmt: Counting, count: number): string => `${fmt.formatCount(count)} selected`,
    selectAllReadable: 'Select everything it says only reads',
    clearSelection: 'Clear',
    applyTitle: 'Switch on what is selected',
    /**
     * The last sentence before several capabilities become callable at once.
     *
     * Three cases, because there are three genuinely different situations and the middle one
     * used to be written as the first. "Changes something" and "stops for a human" are
     * different questions, and the class that answers *yes* to one and *no* to the other —
     * a reversible write — is the one an administrator most needs told about, since it is
     * the only kind that alters the world with nobody watching.
     *
     * The unattended count is stated separately rather than folded in: a reader deciding
     * whether to press this needs to know how much changes AND how much of it changes
     * unsupervised, and one number cannot carry both without rounding toward the
     * reassuring answer.
     */
    applyBody: (
      fmt: Counting,
      { total, writing, unattended }: { total: number; writing: number; unattended: number }
    ): string => {
      const lead = `${fmt.formatCount(total)} will become available to agents that are granted them.`
      if (writing === 0) return `${lead} None of them change anything.`
      const changes = `${fmt.formatCount(writing)} of them change something`
      if (unattended === 0) return `${lead} ${changes}, and a person is asked before each of those runs.`
      return `${lead} ${changes}, and ${fmt.formatCount(unattended)} of those run without anyone being asked.`
    },
    apply: 'Switch them on',
    applying: 'Switching on…',
    applyFailed: (fmt: Counting, count: number): string => `${fmt.formatCount(count)} could not be switched on:`
  },

  // --- system detail --------------------------------------------------------
  detail: {
    diagnose: 'Check this system',
    diagnosing: 'Checking…',
    pause: 'Pause',
    resume: 'Resume',
    pauseConfirm: 'Pause this system?',
    pauseBody:
      'Every agent granted one of its abilities stops being able to reach it, immediately. Work already in flight finishes or fails on its own. Nothing is deleted and nothing is forgotten — resuming puts it all back.',
    remove: 'Remove',
    removeConfirm: 'Remove this system?',
    removeBody:
      'It stops being reachable and disappears from this list. What it offered, and every decision made about it, is kept — so a system removed by mistake comes back with its review history intact.',
    identityTitle: 'Identity',
    connectionTitle: 'Connection',
    protocolLabel: 'Protocol',
    protocolHint: 'The revision of the Model Context Protocol this system and the platform agreed on.',
    eraModern: 'current',
    eraLegacy: 'earlier revision',
    urlLabel: 'Address',
    modeLabel: 'Runs as',
    lastCheckedLabel: 'Last checked',
    offersLabel: 'It also offers',
    offersHint: 'Kinds of thing this system provides that the platform does not yet use.',
    instructionsTitle: 'Guidance it offers',
    instructionsHint: 'Recorded for you. It is never given to any agent.'
  },

  // --- diagnostics ----------------------------------------------------------
  /**
   * The words for the two wire fields that arrive WIDER than the enum they carry.
   *
   * `McpServer.lastProbeStatus` and `CurationQueueItem.risk` are both `z.string()` on the
   * contract, so neither map can be indexed by the value straight off the wire. The lookups in
   * `system-labels.ts` widen the MAP rather than the value — these keep their `satisfies`
   * guard, so a new enum member is still a compile error — and name what happened when the
   * value is one this build has no words for. Before that, the first rendered an empty red
   * badge and the second rendered the identifier.
   */
  probeStage: {
    url_invalid: 'The address',
    ssrf_blocked: 'The address',
    connect: 'Reaching it',
    version_mismatch: 'The protocol',
    token_unresolved: 'Credentials',
    authorization_required: 'Credentials',
    discover: 'The protocol',
    tools_list: 'What it offers'
  } satisfies Record<McpProbeStage, string>,
  /** What a system row says when the probe status is not one of the stages. */
  stageUnhealthy: 'Not working',

  riskLabel: {
    read: 'Only reads',
    reversible_write: 'Changes something that can be undone',
    external_communication: 'Sends something outside',
    privileged: 'Acts with elevated access',
    financial: 'Moves money',
    destructive: 'Destroys something'
  } satisfies Record<RiskClass, string>,

  riskNote: {
    read: 'Runs on its own. It cannot change anything.',
    reversible_write: 'Runs on its own. What it changes can be put back.',
    external_communication: 'A person is asked first. Something leaves the platform when it runs.',
    privileged: 'A person is asked first. It acts with access most people do not have.',
    financial: 'A person is asked first. Money moves when it runs.',
    destructive: 'A person is asked first. What it does cannot be undone.'
  } satisfies Record<RiskClass, string>,

  /** What a queue row says when the class is not one this build knows. */
  riskUnclassified: 'not classified'
}
